import { createContext, useContext, useState, useCallback } from 'react';
import type { ReactNode } from 'react';
import type { ProductWithVariants, CartItem, ModalTriggerSource } from '@/types/cart';

interface AddToCartModalContextType {
  isOpen: boolean;
  product: ProductWithVariants | null;
  triggerSource: ModalTriggerSource | null;
  existingCartItem: CartItem | null;
  openModal: (
    product: ProductWithVariants,
    source: ModalTriggerSource,
    cartItem?: CartItem
  ) => void;
  closeModal: () => void;
}

const AddToCartModalContext = createContext<AddToCartModalContextType | undefined>(undefined);

export function AddToCartModalProvider({ children }: { children: ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);
  const [product, setProduct] = useState<ProductWithVariants | null>(null);
  const [triggerSource, setTriggerSource] = useState<ModalTriggerSource | null>(null);
  const [existingCartItem, setExistingCartItem] = useState<CartItem | null>(null);

  const openModal = useCallback(
    (
      product: ProductWithVariants,
      source: ModalTriggerSource,
      cartItem?: CartItem
    ) => {
      // console.log('openModal called with:', { product, source, cartItem });
      setProduct(product);
      setTriggerSource(source);
      setExistingCartItem(cartItem || null);
      setIsOpen(true);
    },
    []
  );

  const closeModal = useCallback(() => {
    setIsOpen(false);
    // Clear state after animation
    setTimeout(() => {
      setProduct(null);
      setTriggerSource(null);
      setExistingCartItem(null);
    }, 200);
  }, []);

  return (
    <AddToCartModalContext.Provider
      value={{
        isOpen,
        product,
        triggerSource,
        existingCartItem,
        openModal,
        closeModal,
      }}
    >
      {children}
    </AddToCartModalContext.Provider>
  );
}

export function useAddToCartModal() {
  const context = useContext(AddToCartModalContext);
  if (!context) {
    throw new Error('useAddToCartModal must be used within AddToCartModalProvider');
  }
  return context;
}